import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "input", "submitButton", "loadingState", "results", "suggestions"]
  static values = {
    hasResults: Boolean,
    suggestionsUrl: String,
    minQueryLength: { type: Number, default: 2 },
    debounceMs: { type: Number, default: 250 }
  }

  connect() {
    this.abortController = null
    this.debounceTimer = null
    this.hideTimer = null
    this.suggestionItems = []
    this.selectedSuggestionIndex = -1

    if (this.hasResultsTarget && this.hasResultsValue && this.hasResultsValue === true) {
      this.resultsTarget.classList.remove("opacity-0")
    }
  }

  disconnect() {
    this.clearDebounce()
    this.clearHideTimer()
    this.abortInFlightRequest()
  }

  startSearch() {
    this.hideSuggestions()

    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.classList.remove("hidden")
      this.loadingStateTarget.classList.add("flex")
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("btn-disabled")
    }

    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("opacity-50")
    }
  }

  finishSearch() {
    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.classList.add("hidden")
      this.loadingStateTarget.classList.remove("flex")
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove("btn-disabled")
    }

    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove("opacity-50")
      this.resultsTarget.classList.remove("opacity-0")
      this.resultsTarget.classList.add("opacity-100")
    }
  }

  queueSuggestions() {
    if (!this.hasInputTarget || !this.hasSuggestionsUrlValue || !this.hasSuggestionsTarget) return

    this.clearDebounce()
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(), this.debounceMsValue)
  }

  handleKeydown(event) {
    if (!this.hasSuggestionsTarget || this.suggestionsTarget.classList.contains("hidden")) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveSelection(1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveSelection(-1)
      return
    }

    if (event.key === "Enter" && this.selectedSuggestionIndex >= 0) {
      const selected = this.suggestionItems[this.selectedSuggestionIndex]
      if (!selected) return

      event.preventDefault()
      window.location.assign(selected.url)
      return
    }

    if (event.key === "Escape") {
      this.hideSuggestions()
    }
  }

  hideSuggestionsWithDelay() {
    this.clearHideTimer()
    this.hideTimer = setTimeout(() => this.hideSuggestions(), 150)
  }

  async fetchSuggestions() {
    const query = this.inputTarget.value.trim()

    if (query.length < this.minQueryLengthValue) {
      this.hideSuggestions()
      return
    }

    this.abortInFlightRequest()
    this.abortController = new AbortController()

    try {
      const url = `${this.suggestionsUrlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })

      if (!response.ok) {
        this.hideSuggestions()
        return
      }

      const payload = await response.json()
      this.renderSuggestions(payload.suggestions || [])
    } catch (error) {
      if (error.name !== "AbortError") {
        this.hideSuggestions()
      }
    }
  }

  renderSuggestions(suggestions) {
    if (!this.hasSuggestionsTarget) return

    this.suggestionItems = suggestions
    this.selectedSuggestionIndex = -1

    if (suggestions.length === 0) {
      this.hideSuggestions()
      return
    }

    this.suggestionsTarget.innerHTML = suggestions.map((suggestion, index) => {
      const safeTitle = this.escapeHtml(suggestion.title || "")
      const safeExcerpt = this.escapeHtml(suggestion.excerpt || "")
      const safeUrl = this.escapeHtml(suggestion.url || "#")

      return `
        <li>
          <a
            href="${safeUrl}"
            class="rounded-box flex flex-col items-start gap-1 px-3 py-2"
            data-suggestion-index="${index}"
            data-action="mouseenter->tenant-search#highlightSuggestion mousedown->tenant-search#followSuggestion"
          >
            <p class="text-sm font-medium text-base-content">${safeTitle}</p>
            ${safeExcerpt ? `<p class="text-xs text-base-content/70">${safeExcerpt}</p>` : ""}
          </a>
        </li>
      `
    }).join("")

    this.suggestionsTarget.classList.remove("hidden")
  }

  highlightSuggestion(event) {
    const index = Number(event.currentTarget.dataset.suggestionIndex)
    this.selectedSuggestionIndex = Number.isNaN(index) ? -1 : index
    this.syncSelectionStyles()
  }

  followSuggestion(event) {
    event.preventDefault()
    const href = event.currentTarget.getAttribute("href")
    if (!href) return

    window.location.assign(href)
  }

  moveSelection(direction) {
    if (this.suggestionItems.length === 0) return

    if (this.selectedSuggestionIndex < 0) {
      this.selectedSuggestionIndex = direction > 0 ? 0 : this.suggestionItems.length - 1
    } else {
      const nextIndex = this.selectedSuggestionIndex + direction
      const maxIndex = this.suggestionItems.length - 1
      this.selectedSuggestionIndex = Math.max(0, Math.min(maxIndex, nextIndex))
    }

    this.syncSelectionStyles()
  }

  syncSelectionStyles() {
    if (!this.hasSuggestionsTarget) return

    const links = this.suggestionsTarget.querySelectorAll("a[data-suggestion-index]")
    links.forEach((link) => {
      const index = Number(link.dataset.suggestionIndex)
      if (index === this.selectedSuggestionIndex) {
        link.classList.add("menu-active")
      } else {
        link.classList.remove("menu-active")
      }
    })
  }

  hideSuggestions() {
    if (!this.hasSuggestionsTarget) return

    this.suggestionsTarget.classList.add("hidden")
    this.suggestionsTarget.innerHTML = ""
    this.suggestionItems = []
    this.selectedSuggestionIndex = -1
  }

  abortInFlightRequest() {
    if (!this.abortController) return

    this.abortController.abort()
    this.abortController = null
  }

  clearDebounce() {
    if (!this.debounceTimer) return

    clearTimeout(this.debounceTimer)
    this.debounceTimer = null
  }

  clearHideTimer() {
    if (!this.hideTimer) return

    clearTimeout(this.hideTimer)
    this.hideTimer = null
  }

  escapeHtml(value) {
    const element = document.createElement("div")
    element.textContent = value
    return element.innerHTML
  }
}
